import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  StyleSheet,
  Platform,
  ViewStyle,
} from 'react-native';
import DLODropdown from '../DLODropdown';
import { DLODropdownOption } from '../../types/dlo.types';

export interface ScheduleHoursData {
  id: string;
  employee: string;
  role: string;
  shift: string;
  sunday: string;
  monday: string;
  tuesday: string;
  wednesday: string;
  thursday: string;
  friday: string;
  saturday: string;
  total: string;
}

export interface DLOCreateScheduleSHDataProps {
  style?: ViewStyle;
  data?: ScheduleHoursData;
  isChecked?: boolean;
  onCheckboxPress?: () => void;
  onEmployeeChange?: (option: DLODropdownOption | null) => void;
  onRoleChange?: (option: DLODropdownOption | null) => void;
  onShiftChange?: (option: DLODropdownOption | null) => void;
  onHoursChange?: (day: string, value: string) => void;
  employeeOptions?: DLODropdownOption[];
  roleOptions?: DLODropdownOption[];
  shiftOptions?: DLODropdownOption[];
}

const DLOCreateScheduleSHData: React.FC<DLOCreateScheduleSHDataProps> = ({
  style,
  data = {
    id: '1',
    employee: 'Andres Ortiz l 80907440',
    role: 'REPACK',
    shift: '06:30 AM (5x8)',
    sunday: '',
    monday: '8',
    tuesday: '',
    wednesday: '',
    thursday: '',
    friday: '',
    saturday: '',
    total: '10',
  },
  isChecked = true,
  onCheckboxPress,
  onEmployeeChange,
  onRoleChange,
  onShiftChange,
  onHoursChange,
  employeeOptions = [
    { id: '1', label: 'Andres Ortiz l 80907440', value: 'andres_ortiz' },
    { id: '2', label: 'John Smith l 80907441', value: 'john_smith' },
    { id: '3', label: 'Maria Garcia l 80907442', value: 'maria_garcia' },
  ],
  roleOptions = [
    { id: '1', label: 'REPACK', value: 'repack' },
    { id: '2', label: 'PICKER', value: 'picker' },
    { id: '3', label: 'LOADER', value: 'loader' },
  ],
  shiftOptions = [
    { id: '1', label: '06:30 AM (5x8)', value: '06_30_am_5x8' },
    { id: '2', label: '08:00 AM (5x8)', value: '08_00_am_5x8' },
    { id: '3', label: '10:00 AM (5x8)', value: '10_00_am_5x8' },
  ],
}) => {
  const [formData, setFormData] = useState(data);

  const handleHoursChange = (day: string, value: string) => {
    setFormData(prev => ({ ...prev, [day]: value }));
    onHoursChange?.(day, value);
  };

  // Helper function to find the selected option
  const findSelectedOption = (options: DLODropdownOption[], value: string): DLODropdownOption | null => {
    return options.find(option => option.label === value) || null;
  };

  const CheckboxIcon = ({ checked }: { checked: boolean }) => (
    <View style={[styles.checkboxContainer, checked && styles.checkboxChecked]}>
      <View style={[styles.checkbox, checked && styles.checkboxInner]}>
        {checked && (
          <View style={styles.checkboxCheckmark}>
            <Text style={styles.checkmark}>✓</Text>
          </View>
        )}
      </View>
    </View>
  );

  const InputField = ({ 
    value, 
    onChangeText, 
    placeholder,
    width = 51.5,
    backgroundColor = '#FBFCFE'
  }: { 
    value: string; 
    onChangeText?: (text: string) => void; 
    placeholder?: string;
    width?: number;
    backgroundColor?: string;
  }) => (
    <View style={styles.inputContainer}>
      <TextInput
        style={[styles.input, { width, backgroundColor }]}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor="#3A3A3A"
        textAlign="center"
      />
    </View>
  );

  return (
    <View style={[styles.container, style]}>
      {/* Checkbox Section */}
      <View style={styles.checkboxSection}>
        <TouchableOpacity onPress={onCheckboxPress} activeOpacity={0.8}>
          <CheckboxIcon checked={isChecked} />
        </TouchableOpacity>
        <View style={styles.verticalDivider} />
      </View>

      {/* Employee Dropdown */}
                    <DLODropdown
                label=""
                value={findSelectedOption(employeeOptions, data.employee)}
                options={employeeOptions}
                onSelect={(option) => onEmployeeChange?.(Array.isArray(option) ? option[0] : option)}
                style={{ width: 180, paddingVertical: 0, paddingHorizontal: 8 }}
                size='small'
              />

      {/* Role and Shift Sections */}
      <View style={styles.roleShiftSection}>
        <View style={styles.verticalDivider} />
        <DLODropdown
          label=""
          value={findSelectedOption(roleOptions, data.role)}
          options={roleOptions}
          onSelect={(option) => onRoleChange?.(Array.isArray(option) ? option[0] : option)}
          style={{ width: 136, paddingVertical: 0, paddingHorizontal: 8 }}
          size='small'
        />
        <View style={styles.verticalDivider} />
        <DLODropdown
          label=""
          value={findSelectedOption(shiftOptions, data.shift)}
          options={shiftOptions}
          onSelect={(option) => onShiftChange?.(Array.isArray(option) ? option[0] : option)}
          style={{ width: 136, paddingVertical: 0, paddingHorizontal: 8 }}
          size='small'
        />
      </View>

      {/* Hours Input Section */}
      <View style={styles.hoursSection}>
        <View style={styles.verticalDivider} />
        
        {/* Sunday */}
        <InputField
          value={formData.sunday}
          onChangeText={(text) => handleHoursChange('sunday', text)}
          width={64}
        />
        <View style={styles.verticalDivider} />

        {/* Monday */}
        <InputField
          value={formData.monday}
          onChangeText={(text) => handleHoursChange('monday', text)}
          width={64}
        />
        <View style={styles.verticalDivider} />

        {/* Tuesday */}
        <InputField
          value={formData.tuesday}
          onChangeText={(text) => handleHoursChange('tuesday', text)}
          width={64}
        />
        <View style={styles.verticalDivider} />

        {/* Wednesday */}
        <InputField
          value={formData.wednesday}
          onChangeText={(text) => handleHoursChange('wednesday', text)}
          width={64}
        />
        <View style={styles.verticalDivider} />

        {/* Thursday */}
        <InputField
          value={formData.thursday}
          onChangeText={(text) => handleHoursChange('thursday', text)}
          width={64}
        />
        <View style={styles.verticalDivider} />

        {/* Friday */}
        <InputField
          value={formData.friday}
          onChangeText={(text) => handleHoursChange('friday', text)}
          width={64}
        />
        <View style={styles.verticalDivider} />

        {/* Saturday */}
        <InputField
          value={formData.saturday}
          onChangeText={(text) => handleHoursChange('saturday', text)}
          width={64}
        />
        <View style={styles.verticalDivider} />

        {/* Total */}
        <InputField
          value={formData.total}
          onChangeText={(text) => handleHoursChange('total', text)}
          width={64}
          backgroundColor="#EFEFEF"
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: 1231,
    height: 40,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#EFF5FC',
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderTopColor: '#DFDFDF',
    borderBottomColor: '#DFDFDF',
  },
  // Checkbox Section
  checkboxSection: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 40,
  },
  checkboxContainer: {
    width: 48,
    height: 40,
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  checkbox: {
    width: 12,
    height: 12,
    borderWidth: 1,
    borderColor: '#005CBC',
    borderRadius: 2,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  checkboxChecked: {
    // Styles for checked state if needed
  },
  checkboxInner: {
    backgroundColor: '#005CBC',
    borderColor: '#005CBC',
  },
  checkboxCheckmark: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkmark: {
    color: '#FFFFFF',
    fontSize: 8,
    fontWeight: 'bold',
  },
  // Role and Shift Section
  roleShiftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 40,
  },
  // Hours Section
  hoursSection: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 40,
    flex: 1,
    gap: 8,
  },
  // Input Styles
  inputContainer: {
    width: 51.5,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    margin: 'auto',
  },
  input: {
    height: 24,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#C0C0C0',
    backgroundColor: '#FBFCFE',
    paddingHorizontal: 8,
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
    margin: 'auto',
  },
  // Dividers
  verticalDivider: {
    width: 1,
    height: 40,
    backgroundColor: '#DFDFDF',
  },
});

export default DLOCreateScheduleSHData;
